import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { useCompany, useDeleteTaxRate, useTaxRates, useUpsertTaxRate } from "@/features/settings/api";
import {
  GST_SLABS,
  TAX_TYPES,
  labelFor,
  listGstSlabs,
  resolveApplicableTaxes,
} from "@/features/settings/constants";
import { emptyTaxForm, taxFormSchema, type TaxFormValues } from "@/features/settings/schema";
import type { TaxRate } from "@/features/settings/types";
import { INDIAN_STATES } from "@/features/clients/constants";

export function TaxesSettingsPage() {
  return (
    <ErrorBoundary title="Taxes page crashed">
      <TaxesSettingsInner />
    </ErrorBoundary>
  );
}

function TaxesSettingsInner() {
  const { data = [], isLoading, error } = useTaxRates();
  const { data: company } = useCompany();
  const upsert = useUpsertTaxRate();
  const remove = useDeleteTaxRate();
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("active");
  const [editing, setEditing] = useState<TaxRate | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<TaxRate | null>(null);

  const [previewSlab, setPreviewSlab] = useState(18);
  const [previewPos, setPreviewPos] = useState(company?.state ?? "");

  const form = useForm<TaxFormValues>({
    resolver: zodResolver(taxFormSchema),
    defaultValues: emptyTaxForm,
  });

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        if (statusFilter === "active" && !row.is_active) return false;
        if (statusFilter === "inactive" && row.is_active) return false;
        if (typeFilter !== "all" && row.tax_type !== typeFilter) return false;
        return true;
      }),
    [data, statusFilter, typeFilter],
  );

  const slabs = useMemo(() => listGstSlabs(data), [data]);

  const preview = useMemo(
    () =>
      resolveApplicableTaxes({
        rates: data,
        gstSlab: previewSlab,
        companyState: company?.state,
        clientPlaceOfSupply: previewPos || company?.state,
      }),
    [data, previewSlab, company?.state, previewPos],
  );

  function openCreate() {
    setEditing(null);
    form.reset(emptyTaxForm);
    setOpen(true);
  }

  function openEdit(row: TaxRate) {
    setEditing(row);
    form.reset({
      name: row.name,
      tax_code: row.tax_code,
      rate: row.rate,
      tax_type: row.tax_type,
      gst_slab: row.gst_slab,
      effective_from: row.effective_from,
      effective_to: row.effective_to ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  const watchedType = form.watch("tax_type");

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-border bg-card px-2.5 py-2 text-2xs text-muted-foreground">
        <p className="text-xs font-medium text-foreground">How invoicing will use these</p>
        <p className="mt-0.5">
          Pick a <span className="text-foreground">GST slab</span> on the line. Same state →{" "}
          <span className="text-foreground">CGST + SGST</span>. Different →{" "}
          <span className="text-foreground">IGST</span>.
        </p>
        <div className="mt-1.5 flex flex-wrap items-end gap-2">
          <label className="space-y-0.5">
            <span className="field-label">Preview slab</span>
            <NativeSelect
              value={String(previewSlab)}
              onChange={(e) => setPreviewSlab(Number(e.target.value))}
              className="w-24"
            >
              {(slabs.length ? slabs : [...GST_SLABS]).map((s) => (
                <option key={s} value={s}>
                  {s}%
                </option>
              ))}
            </NativeSelect>
          </label>
          <label className="space-y-0.5">
            <span className="field-label">Client POS</span>
            <NativeSelect
              value={previewPos}
              onChange={(e) => setPreviewPos(e.target.value)}
              className="w-40"
            >
              <option value="">Same as company</option>
              {INDIAN_STATES.map((s) => (
                <option key={s.code} value={s.name}>
                  {s.name}
                </option>
              ))}
            </NativeSelect>
          </label>
          <p className="pb-1">
            →{" "}
            <span className="font-medium text-foreground">
              {preview.mode === "intra_state" ? "Intra" : "Inter"}
            </span>
            {": "}
            {preview.taxes.length
              ? preview.taxes.map((t) => `${t.tax_code} ${t.rate}%`).join(" + ")
              : "no rates"}
            {preview.taxes.length ? ` = ${preview.totalRate}%` : ""}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <NativeSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            className="w-28"
            aria-label="Status filter"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </NativeSelect>
          <NativeSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-36"
            aria-label="Type filter"
          >
            <option value="all">All types</option>
            {TAX_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </NativeSelect>
          <span className="text-2xs tabular-nums text-muted-foreground">{filtered.length} shown</span>
        </div>
        <Button type="button" size="sm" onClick={openCreate}>
          <Plus />
          Add tax
        </Button>
      </div>

      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-2xs text-destructive">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="overflow-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Type</th>
              <th className="px-3 py-2 text-right font-semibold">Component</th>
              <th className="px-3 py-2 text-right font-semibold">Slab</th>
              <th className="px-3 py-2 font-semibold">Effective</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  No tax rates
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="border-b border-border/70">
                  <td className="px-3 py-1.5 font-mono text-xs font-medium">{row.tax_code}</td>
                  <td className="px-3 py-1.5 font-medium">{row.name}</td>
                  <td className="px-3 py-1.5">
                    <Badge variant="outline">{labelFor(TAX_TYPES, row.tax_type)}</Badge>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular">{row.rate}%</td>
                  <td className="px-3 py-1.5 text-right tabular text-xs text-muted-foreground">
                    {row.gst_slab !== null && row.gst_slab !== undefined ? `${row.gst_slab}%` : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">
                    {format(parseISO(row.effective_from), "dd MMM yyyy")}
                    {row.effective_to
                      ? ` – ${format(parseISO(row.effective_to), "dd MMM yyyy")}`
                      : " →"}
                  </td>
                  <td className="px-3 py-1.5">
                    <StatusDot tone={row.is_active ? "success" : "neutral"}>
                      {row.is_active ? "Active" : "Inactive"}
                    </StatusDot>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${row.tax_code}`}
                      onClick={() => openEdit(row)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Delete ${row.tax_code}`}
                      className="hover:text-destructive"
                      onClick={() => setDeleting(row)}
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit tax rate" : "New tax rate"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Code"
                required
                span={4}
                error={form.formState.errors.tax_code?.message}
                htmlFor="tax-code"
              >
                <Input id="tax-code" className="uppercase" {...form.register("tax_code")} />
              </Field>
              <Field
                label="Name"
                required
                span={8}
                error={form.formState.errors.name?.message}
                htmlFor="tax-name"
              >
                <Input id="tax-name" {...form.register("name")} />
              </Field>
              <Field label="Type" span={4} htmlFor="tax-type">
                <NativeSelect id="tax-type" {...form.register("tax_type")}>
                  {TAX_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Component %"
                required
                span={4}
                error={form.formState.errors.rate?.message}
                hint="CGST/SGST half of slab; IGST = full slab"
                htmlFor="tax-rate"
              >
                <Input
                  id="tax-rate"
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  {...form.register("rate")}
                />
              </Field>
              <Field
                label="GST slab %"
                span={4}
                error={form.formState.errors.gst_slab?.message}
                hint={
                  watchedType === "cgst" || watchedType === "sgst" || watchedType === "igst"
                    ? "Required — e.g. 18 for CGST9+SGST9 / IGST18"
                    : "Optional for non-GST types"
                }
                htmlFor="tax-slab"
              >
                <NativeSelect id="tax-slab" {...form.register("gst_slab")}>
                  <option value="">—</option>
                  {GST_SLABS.map((s) => (
                    <option key={s} value={s}>
                      {s}%
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={12}
              />
              <Field
                label="Effective from"
                required
                span={6}
                error={form.formState.errors.effective_from?.message}
                htmlFor="tax-from"
              >
                <Input id="tax-from" type="date" {...form.register("effective_from")} />
              </Field>
              <Field
                label="Effective to"
                span={6}
                error={form.formState.errors.effective_to?.message}
                hint="Leave blank if open-ended"
                htmlFor="tax-to"
              >
                <Input id="tax-to" type="date" {...form.register("effective_to")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete tax rate?"
        description={
          deleting
            ? `${deleting.tax_code} will be removed. Prefer deactivate if used on documents.`
            : undefined
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
